import { z } from 'zod';
import { v1UserRefSchema } from './actor';
import { v1AlertOutcomeSchema, v1AlertSeveritySchema } from './alerts';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
/**
 * Session alerts for `/api/v1` (KIR-4785): every alert raised in the agent
 * session an alert occurrence came from. **Published**: every field here is
 * frozen (see `contract-snapshot.test.ts`); add fields, never change them.
 */
export const v1SessionAlertsQuerySchema = v1PaginationQuerySchema;
export const v1SessionAlertSchema = z
    .object({
    id: z
        .string()
        .describe("This occurrence's ID. Pass it as `occurrenceId` to list its session's alerts again."),
    alertId: z
        .string()
        .describe('The alert this occurrence belongs to: the same `id` the alerts list returns.'),
    occurredAt: z
        .string()
        .datetime()
        .describe('When the agent event that raised it happened (ISO 8601 UTC), or when the alert was recorded if that event is no longer stored. Alerts are listed in this order, oldest first.'),
    type: z
        .string()
        .describe('The stable machine identifier for the kind of finding.'),
    title: z.string().describe("The finding's name as the Kirin app shows it."),
    policyKey: z
        .string()
        .nullable()
        .describe('The Kirin policy that produced the alert. Null for legacy findings that predate policy mapping.'),
    policyName: z
        .string()
        .nullable()
        .describe("That policy's name as the Kirin app shows it."),
    subPolicyKey: z
        .string()
        .nullable()
        .describe('The specific rule within that policy, as a stable key.'),
    subPolicyName: z
        .string()
        .nullable()
        .describe("That rule's name as the Kirin app shows it."),
    severity: v1AlertSeveritySchema,
    outcome: v1AlertOutcomeSchema,
    filePath: z
        .string()
        .nullable()
        .describe('Path of the file the finding is in, or null when there is none.'),
    endpointName: z
        .string()
        .nullable()
        .describe('The name of the machine it happened on, as recorded with the alert. Null when not recorded or not enabled for the organization.'),
    muted: z
        .boolean()
        .describe('Whether the alert is muted. Muted alerts are listed; excluded alerts are not.'),
})
    .meta({ id: 'V1SessionAlert', title: 'Session alert' });
export const v1SessionAlertsSessionSchema = z
    .object({
    sessionId: z.string().describe("The agent's ID for the session."),
    user: v1UserRefSchema
        .nullable()
        .describe("The session's user. Null once that user was deleted."),
    alertCount: z
        .number()
        .int()
        .min(0)
        .describe('Alerts listed for the session, across every page.'),
    firstAlertAt: z
        .string()
        .datetime()
        .nullable()
        .describe('`occurredAt` of the earliest alert. Null when none is listed.'),
    lastAlertAt: z
        .string()
        .datetime()
        .nullable()
        .describe('`occurredAt` of the latest alert. Null when none is listed.'),
    endpointName: z
        .string()
        .nullable()
        .describe("The latest alert's `endpointName`. Null when none is listed."),
    eventCount: z
        .number()
        .int()
        .min(0)
        .nullable()
        .describe("Agent events stored for the session. Null when the session's events are not currently available."),
    blockedCount: z
        .number()
        .int()
        .min(0)
        .nullable()
        .describe("Events the agent was stopped on. Null when the session's events are not currently available."),
})
    .meta({ id: 'V1SessionAlertsSession', title: 'Session summary' });
export const v1SessionAlertsPageSchema = z
    .object({
    session: v1SessionAlertsSessionSchema,
    data: z.array(v1SessionAlertSchema).describe('This page of alerts.'),
    pagination: v1PaginationSchema,
})
    .meta({ id: 'V1SessionAlertsPage', title: 'Session alerts page' });
//# sourceMappingURL=session-alerts.js.map